package kz.dentalcrm.app.screens.appointments.edit

import androidx.compose.runtime.Immutable
import androidx.compose.runtime.Stable
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.at
import dentalcrm.composeapp.generated.resources.error_message
import dentalcrm.composeapp.generated.resources.snackbar_appointment_updated
import dentalcrm.composeapp.generated.resources.month_eight_short
import dentalcrm.composeapp.generated.resources.month_eleven_short
import dentalcrm.composeapp.generated.resources.month_five_short
import dentalcrm.composeapp.generated.resources.month_four_short
import dentalcrm.composeapp.generated.resources.month_nine_short
import dentalcrm.composeapp.generated.resources.month_one_short
import dentalcrm.composeapp.generated.resources.month_seven_short
import dentalcrm.composeapp.generated.resources.month_six_short
import dentalcrm.composeapp.generated.resources.month_ten_short
import dentalcrm.composeapp.generated.resources.month_three_short
import dentalcrm.composeapp.generated.resources.month_twelve_short
import dentalcrm.composeapp.generated.resources.month_two_short
import kotlinx.collections.immutable.PersistentList
import kotlinx.collections.immutable.persistentListOf
import kotlinx.collections.immutable.toPersistentList
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDateTime
import kotlin.random.Random
import kz.dentalcrm.app.core.base.BaseStateViewModel
import kz.dentalcrm.app.core.base.Selectable
import kz.dentalcrm.app.core.base.ViewEvent
import kz.dentalcrm.app.core.base.ViewSideEffect
import kz.dentalcrm.app.core.ui.InputValue
import kz.dentalcrm.app.core.utils.Result
import kz.dentalcrm.app.core.utils.stateHandle
import kz.dentalcrm.app.data.network.request_model.AppointmentRequest
import kz.dentalcrm.app.data.network.request_model.AppointmentService
import kz.dentalcrm.app.data.network.response_model.CashBox
import kz.dentalcrm.app.data.network.response_model.PatientData
import kz.dentalcrm.app.data.network.response_model.PatientTreatment
import kz.dentalcrm.app.data.network.response_model.TreatmentCourse
import kz.dentalcrm.app.data.network.response_model.employees.Employee
import kz.dentalcrm.app.data.network.response_model.services.Service
import kz.dentalcrm.app.domain.AppointmentRepository
import kz.dentalcrm.app.core.utils.pad
import kz.dentalcrm.app.core.utils.parseServerDateTime
import kz.dentalcrm.app.core.utils.plusMinutes
import kz.dentalcrm.app.core.utils.toSqlString
import kz.dentalcrm.app.screens.appointments.add.getWeekDay
import kz.dentalcrm.app.theme.SnackbarController
import kz.dentalcrm.app.theme.SnackbarEvent
import org.jetbrains.compose.resources.getString
import org.koin.core.component.inject

data class TreatmentState(
    val selectedTeeth: List<String> = emptyList(),
    val treatments: List<PatientTreatment> = emptyList(),
    val isLoadingTreatments: Boolean = false
)

data class SelectedService(
    val uniqueId: Long,
    val service: Service,
    val quantity: Int = 1,
    val discount: Int = 0,
    val price: Int = service.price ?: 0
) {
    val total: Int
        get() {
            val subtotal = price * quantity
            return subtotal - (subtotal * discount / 100)
        }
}

data class ServicesState(
    val allServices: List<Service> = emptyList(),
    val selectedServices: List<SelectedService> = emptyList(),
    val isLoadingServices: Boolean = false
)

@Immutable
@Stable
data class EditAppointmentState(
    val isLoading: Boolean = false,
    val isInitialLoadComplete: Boolean = false,
    val isSaving: Boolean = false,
    val appointmentId: Int = 0,
    val selectedPatient: PatientData? = null,
    val dateFormatted: String? = null,
    val selectDateTime: LocalDateTime? = null,
    val employees: PersistentList<Selectable<Employee>> = persistentListOf(),
    val cashBoxes: PersistentList<CashBox> = persistentListOf(),
    val cashBoxId: Int? = null,
    val status: String = "pending",
    val suggestedPatients: PersistentList<PatientData> = persistentListOf(),
    val searchPatientInput: InputValue = InputValue(),
    val commentInput: InputValue = InputValue(),
    val enableSave: Boolean = false,
    val selectedTabIndex: Int = 0,
    val treatmentState: TreatmentState = TreatmentState(),
    val servicesState: ServicesState = ServicesState(),
    val initialInspection: kz.dentalcrm.app.data.network.response_model.InitialInspection? = null
)

sealed interface EditAppointmentEvent : ViewEvent {
    data class LoadData(val id: Int) : EditAppointmentEvent
    data object Save : EditAppointmentEvent
    data object OnBack : EditAppointmentEvent
    data class SearchPatient(val search: String) : EditAppointmentEvent
    data class SelectPatient(val patient: PatientData) : EditAppointmentEvent
    data object RemovePatient : EditAppointmentEvent
    data class SelectDateTime(val dateTime: LocalDateTime) : EditAppointmentEvent
    data class SelectEmployee(val employee: Employee) : EditAppointmentEvent
    data class CommentChanged(val value: String) : EditAppointmentEvent
    data class SelectTab(val index: Int) : EditAppointmentEvent
    data class ToggleTooth(val toothId: String) : EditAppointmentEvent
    // Services events
    data object LoadServices : EditAppointmentEvent
    data class AddService(val service: Service) : EditAppointmentEvent
    data class RemoveService(val uniqueId: Long) : EditAppointmentEvent
    data class UpdateServiceQuantity(val uniqueId: Long, val quantity: Int) : EditAppointmentEvent
    data class UpdateServiceDiscount(val uniqueId: Long, val discount: Int) : EditAppointmentEvent
    data class UpdateServicePrice(val uniqueId: Long, val price: Int) : EditAppointmentEvent
}

sealed interface EditAppointmentEffect : ViewSideEffect {
    data class ShowError(val message: String) : EditAppointmentEffect
    data object NavigateBack : EditAppointmentEffect
}

class EditAppointmentViewModel :
    BaseStateViewModel<EditAppointmentEvent, EditAppointmentState, EditAppointmentEffect>() {

    private val repository: AppointmentRepository by inject()
    private var searchPatientJob: Job? = null

    override fun setInitialState(): EditAppointmentState = EditAppointmentState()

    override fun handleEvents(event: EditAppointmentEvent) {
        when (event) {
            is EditAppointmentEvent.LoadData -> loadData(event.id)
            is EditAppointmentEvent.Save -> save()
            is EditAppointmentEvent.OnBack -> setEffect { EditAppointmentEffect.NavigateBack }
            is EditAppointmentEvent.SearchPatient -> {
                setState { copy(searchPatientInput = InputValue(value = event.search)) }
                searchPatients(event.search)
            }

            is EditAppointmentEvent.SelectPatient -> selectPatient(event.patient)
            is EditAppointmentEvent.RemovePatient -> removePatient()
            is EditAppointmentEvent.SelectDateTime -> selectDateTime(event.dateTime)
            is EditAppointmentEvent.SelectEmployee -> selectEmployee(event.employee)
            is EditAppointmentEvent.CommentChanged -> commentChanged(event.value)
            is EditAppointmentEvent.SelectTab -> selectTab(event.index)
            is EditAppointmentEvent.ToggleTooth -> toggleTooth(event.toothId)
            // Services
            is EditAppointmentEvent.LoadServices -> loadAllServices()
            is EditAppointmentEvent.AddService -> addService(event.service)
            is EditAppointmentEvent.RemoveService -> removeService(event.uniqueId)
            is EditAppointmentEvent.UpdateServiceQuantity -> updateServiceQuantity(event.uniqueId, event.quantity)
            is EditAppointmentEvent.UpdateServiceDiscount -> updateServiceDiscount(event.uniqueId, event.discount)
            is EditAppointmentEvent.UpdateServicePrice -> updateServicePrice(event.uniqueId, event.price)
        }
    }

    private fun loadData(id: Int) {
        setState { copy(isLoading = true, appointmentId = id) }
        launch {
            repository.getCashBoxes().stateHandle { cashBoxesList ->
                val cashBoxes = cashBoxesList.toPersistentList()
                setState { copy(cashBoxes = cashBoxes) }
            }

            repository.getEmployees(page = 0, pageSize = 100).stateHandle { employeesData ->
                val employees = employeesData?.data
                    ?.map { Selectable(data = it, selected = false) }
                    ?.toPersistentList() ?: persistentListOf()

                setState { copy(employees = employees) }

                when (val result = repository.getAppointment(id)) {
                    is Result.Success -> {
                        val appointment = result.value
                        if (appointment != null) {
                            val patient = appointment.patient

                            val dateTime = appointment.starts_at?.parseServerDateTime()

                            val selectedEmployeeId = appointment.employee?.id
                            val updatedEmployees = employees.map {
                                it.copy(selected = it.data.id == selectedEmployeeId)
                            }.toPersistentList()

                            // Load services from appointment - convert to services.Service
                            var uniqueIdCounter = Random.nextLong()
                            val selectedServices = appointment.services?.map { responseService ->
                                val convertedService = Service(
                                    category_id = responseService.category_id,
                                    created_at = responseService.created_at,
                                    description = responseService.description,
                                    duration = responseService.duration,
                                    id = responseService.id,
                                    max_price = responseService.max_price,
                                    name = responseService.name,
                                    organization_id = responseService.organization_id,
                                    pivot = null,
                                    price = responseService.price,
                                    updated_at = responseService.updated_at
                                )
                                SelectedService(
                                    uniqueId = uniqueIdCounter++,
                                    service = convertedService,
                                    quantity = responseService.pivot?.amount ?: 1,
                                    discount = responseService.pivot?.discount ?: 0,
                                    price = responseService.pivot?.actual_price ?: responseService.price ?: 0
                                )
                            } ?: emptyList()

                            setState {
                                copy(
                                    isLoading = false,
                                    isInitialLoadComplete = true,
                                    status = appointment.status ?: "pending",
                                    selectedPatient = patient,
                                    employees = updatedEmployees,
                                    appointmentId = appointment.id,
                                    cashBoxId = appointment.cash_box_id
                                        ?: viewState.value.cashBoxes.firstOrNull { it.isMain }?.id,
                                    commentInput = InputValue(value = appointment.comment ?: ""),
                                    selectDateTime = dateTime,
                                    initialInspection = appointment.initial_inspection,
                                    servicesState = servicesState.copy(selectedServices = selectedServices)
                                )
                            }
                            if (dateTime != null) {
                                selectDateTime(dateTime)
                                updateDateFormatted(dateTime)
                            }
                            checkButtonState()
                            loadTreatments(id)
                        } else {
                            setState { copy(isLoading = false) }
                            setEffect { EditAppointmentEffect.ShowError("Appointment not found") }
                        }
                    }

                    is Result.Error -> {
                        setState { copy(isLoading = false) }
                        setEffect {
                            EditAppointmentEffect.ShowError(
                                result.message ?: "Error loading appointment"
                            )
                        }
                    }

                    else -> {
                        setState { copy(isLoading = false) }
                        setEffect { EditAppointmentEffect.ShowError("Unknown error") }
                    }
                }
            }
        }
    }

    private fun loadTreatments(appointmentId: Int) {
        setState {
            copy(treatmentState = treatmentState.copy(isLoadingTreatments = true))
        }
        launch {
            when (val result = repository.getAppointmentTreatments(appointmentId)) {
                is Result.Success -> {
                    val treatments = result.value
                    val allTeeth = treatments.flatMap { treatment ->
                        treatment.teeth.map { it.toString() }
                    }.distinct()

                    setState {
                        copy(
                            treatmentState = treatmentState.copy(
                                treatments = treatments,
                                selectedTeeth = allTeeth,
                                isLoadingTreatments = false
                            )
                        )
                    }
                }
                is Result.Error -> {
                    setState {
                        copy(treatmentState = treatmentState.copy(isLoadingTreatments = false))
                    }
                }
                else -> {
                    setState {
                        copy(treatmentState = treatmentState.copy(isLoadingTreatments = false))
                    }
                }
            }
        }
    }

    private fun searchPatients(query: String) {
        searchPatientJob?.cancel()
        if (query.isNotEmpty()) {
            searchPatientJob = launch {
                delay(1000L)
                repository.searchPatients(query)
                    .stateHandle { data ->
                        setState {
                            copy(suggestedPatients = data.data.take(5).toPersistentList())
                        }
                    }
            }
        } else {
            setState {
                copy(suggestedPatients = persistentListOf())
            }
        }
    }

    private fun selectPatient(patient: PatientData) {
        setState { copy(selectedPatient = patient) }
        checkButtonState()
    }

    private fun removePatient() {
        setState { copy(selectedPatient = null) }
        checkButtonState()
    }

    private fun selectDateTime(dateTime: LocalDateTime) {
        setState { copy(selectDateTime = dateTime) }
        updateDateFormatted(dateTime)
        checkButtonState()
    }

    private fun updateDateFormatted(dateTime: LocalDateTime) {
        launch {
            val months = listOf(
                getString(Res.string.month_one_short),
                getString(Res.string.month_two_short),
                getString(Res.string.month_three_short),
                getString(Res.string.month_four_short),
                getString(Res.string.month_five_short),
                getString(Res.string.month_six_short),
                getString(Res.string.month_seven_short),
                getString(Res.string.month_eight_short),
                getString(Res.string.month_nine_short),
                getString(Res.string.month_ten_short),
                getString(Res.string.month_eleven_short),
                getString(Res.string.month_twelve_short),
            )
            val at = getString(Res.string.at)
            val weekDay = dateTime.dayOfWeek.ordinal.getWeekDay()
            setState {
                copy(
                    dateFormatted = "${dateTime.day} ${months[dateTime.month.ordinal]} ${dateTime.year} $at ${dateTime.hour}:${dateTime.minute.pad()} ($weekDay)"
                )
            }
        }
    }

    private fun selectEmployee(employee: Employee) {
        val employees = viewState.value.employees.map {
            it.copy(selected = it.data.id == employee.id)
        }
        setState { copy(employees = employees.toPersistentList()) }
        checkButtonState()
    }

    private fun commentChanged(value: String) {
        setState { copy(commentInput = InputValue(value = value)) }
    }

    private fun selectTab(index: Int) {
        setState { copy(selectedTabIndex = index) }
    }

    private fun toggleTooth(toothId: String) {
        val currentTeeth = viewState.value.treatmentState.selectedTeeth
        val newTeeth = if (currentTeeth.contains(toothId)) {
            currentTeeth - toothId
        } else {
            currentTeeth + toothId
        }
        setState { copy(treatmentState = treatmentState.copy(selectedTeeth = newTeeth)) }
    }

    // Services methods
    private fun loadAllServices() {
        // Don't reload if already loaded
        if (viewState.value.servicesState.allServices.isNotEmpty()) return

        setState { copy(servicesState = servicesState.copy(isLoadingServices = true)) }
        launch {
            repository.getServicesArray().stateHandle { services ->
                setState {
                    copy(servicesState = servicesState.copy(
                        allServices = services,
                        isLoadingServices = false
                    ))
                }
            }
        }
    }

    private fun addService(service: Service) {
        val currentServices = viewState.value.servicesState.selectedServices
        val newService = SelectedService(
            uniqueId = Random.nextLong(),
            service = service,
            quantity = 1,
            discount = 0,
            price = service.price ?: 0
        )
        setState {
            copy(servicesState = servicesState.copy(
                selectedServices = currentServices + newService
            ))
        }
    }

    private fun removeService(uniqueId: Long) {
        val currentServices = viewState.value.servicesState.selectedServices
        setState {
            copy(servicesState = servicesState.copy(
                selectedServices = currentServices.filter { it.uniqueId != uniqueId }
            ))
        }
    }

    private fun updateServiceQuantity(uniqueId: Long, quantity: Int) {
        if (quantity < 1) return
        val currentServices = viewState.value.servicesState.selectedServices
        setState {
            copy(servicesState = servicesState.copy(
                selectedServices = currentServices.map {
                    if (it.uniqueId == uniqueId) it.copy(quantity = quantity) else it
                }
            ))
        }
    }

    private fun updateServiceDiscount(uniqueId: Long, discount: Int) {
        if (discount < 0 || discount > 100) return
        val currentServices = viewState.value.servicesState.selectedServices
        setState {
            copy(servicesState = servicesState.copy(
                selectedServices = currentServices.map {
                    if (it.uniqueId == uniqueId) it.copy(discount = discount) else it
                }
            ))
        }
    }

    private fun updateServicePrice(uniqueId: Long, price: Int) {
        if (price < 0) return
        val currentServices = viewState.value.servicesState.selectedServices
        setState {
            copy(servicesState = servicesState.copy(
                selectedServices = currentServices.map {
                    if (it.uniqueId == uniqueId) it.copy(price = price) else it
                }
            ))
        }
    }

    private fun checkButtonState() {
        val currentState = viewState.value
        setState {
            copy(
                enableSave = currentState.selectedPatient != null &&
                        currentState.selectDateTime != null &&
                        currentState.employees.any { it.selected }
            )
        }
    }

    private fun save() {
        val currentState = viewState.value
        if (currentState.isSaving || !currentState.isInitialLoadComplete) return
        val appointmentId = currentState.appointmentId
        if (appointmentId == 0) return

        setState { copy(isSaving = true) }
        launch {
            val startDateTime = currentState.selectDateTime
            val endDateTime = startDateTime?.plusMinutes(20)
            val patient = currentState.selectedPatient?.copy(
                patronymic = if (currentState.selectedPatient.patronymic.isNullOrEmpty()) "  "
                else currentState.selectedPatient.patronymic
            )
            val title = currentState.commentInput.value

            val treatmentCourseName = currentState.selectedPatient?.let { p ->
                "${p.surname.orEmpty()} ${p.name.orEmpty()} ${p.patronymic.orEmpty()}, ${p.phone.orEmpty()}".trim()
            }

            val treatmentCourse = TreatmentCourse(
                id = appointmentId,
                name = treatmentCourseName,
                is_finished = 0,
                created_at = null,
                updated_at = null,
                deleted_at = null
            )

            // Convert selected services to AppointmentService
            val services = currentState.servicesState.selectedServices.mapNotNull { selected ->
                val serviceId = selected.service.id ?: return@mapNotNull null
                AppointmentService(
                    discount = selected.discount,
                    id = serviceId,
                    overallPrice = selected.total,
                    price = selected.price,
                    quantity = selected.quantity,
                    service = kz.dentalcrm.app.data.network.request_model.Service(
                        category = null,
                        category_id = selected.service.category_id,
                        created_at = selected.service.created_at,
                        description = selected.service.description,
                        duration = selected.service.duration,
                        id = serviceId,
                        max_price = selected.service.max_price,
                        name = selected.service.name,
                        organization_id = selected.service.organization_id,
                        price = selected.service.price,
                        updated_at = selected.service.updated_at
                    )
                )
            }

            val totalPrice = currentState.servicesState.selectedServices.sumOf { it.total }

            val request = AppointmentRequest(
                cash_box_id = currentState.cashBoxId,
                employee = currentState.employees.firstOrNull { it.selected }?.data,
                ends_at = endDateTime?.toSqlString(),
                is_first_visit = false,
                patient = patient,
                price = totalPrice,
                services = services,
                status = currentState.status,
                comment = currentState.commentInput.value.takeIf { it.isNotEmpty() },
                starts_at = startDateTime?.toSqlString(),
                title = title.ifEmpty { null },
                treatment_course = treatmentCourse
            )

            when (val result = repository.updateAppointment(appointmentId, request)) {
                is Result.Success -> {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.snackbar_appointment_updated), SnackbarEvent.SnackbarType.Green))
                    setState { copy(isSaving = false) }
                    setEffect { EditAppointmentEffect.NavigateBack }
                }

                is Result.Error -> {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState { copy(isSaving = false) }
                    setEffect {
                        EditAppointmentEffect.ShowError(
                            result.message ?: "Error saving appointment"
                        )
                    }
                }

                else -> {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState { copy(isSaving = false) }
                    setEffect { EditAppointmentEffect.ShowError("Unknown error") }
                }
            }
        }
    }
}
